<template>
  <div :class="theme" class="table-wrapper" :data-cy="dataCy">
    <div
      v-show="isShownFixedOrActionsHeader"
      ref="headerFixedRow"
      class="table-custom-header"
      :class="[headerClasses.actionsHeader, headerClasses.fixedHeader, headerClasses.compact]"
      :style="tableRowWidthStyle"
    >
      <OurCheckbox
        v-if="selectable"
        v-model="selectAll"
        :partial="!isSelectedAllRows"
        description="Усі"
        class="table-custom-header-item"
        size="sm"
        :disabled="!canSelectAllProps"
      />

      <template v-if="hasSelectionActionsHeader">
        <div class="selected-rows-count">
          {{ selectedRowsLength }}
        </div>

        <div class="actions-slot-wrap">
          <!-- @slot Use it to add actions instead table head (it appears when you select items in table). -->
          <slot name="thead-actions" :selected-rows="selectedRows" />
        </div>
      </template>

      <template v-else>
        <div
          v-for="(item, index) in headers"
          :key="index"
          class="table-custom-header-item"
          :class="item.thClass"
        >
          <template v-if="isExistSlot(`thead-${item.value}`)">
            <!-- @slot Use it to customise table header item. -->
            <slot :name="`thead-${item.value}`" :text="item.text" />
          </template>

          <template v-else>
            <div v-html="item.text"></div>
          </template>
        </div>
      </template>

      <OurTopLoader
        v-if="loaderResourceName"
        class="fixed-header-table-loader"
        :resource-names="loaderResourceName"
        position="absolute"
      />
    </div>

    <div ref="tableWrapper" class="table-wrap" :class="tableWrapClass">
      <t-table
        class="our-table"
        :data="tableItems"
        :headers="headers"
        :class="tableClasses"
        :hide-header="hiddenHeader"
      >
        <template v-if="!hiddenHeader" #thead="{ thClass, data }">
          <thead>
            <tr ref="headerRow" :class="headerClasses.hideHeader">
              <th v-if="selectable" class="table-checkbox">
                <OurCheckbox
                  v-model="selectAll"
                  size="sm"
                  :show-partial-icon="!isSelectedAllRows"
                  description="Усі"
                  partial
                  :data-cy="`${dataCy}-select-all`"
                  :disabled="!canSelectAllProps"
                />
              </th>

              <th v-for="(item, index) in data" :key="index" :class="[thClass, item.thClass]">
                <template v-if="isExistSlot(`thead-${item.value}`)">
                  <!-- @slot Use it to customise table header item. -->
                  <slot :name="`thead-${item.value}`" :text="item.text" />
                </template>

                <template v-else>
                  <div
                    v-if="item.sortable"
                    class="inline-flex cursor-pointer items-center gap-1"
                    @click="$emit('sort', item)"
                  >
                    <div v-html="item.text"></div>
                    <div class="flex flex-col">
                      <OurSvgIcon
                        class="-mr-2 rotate-90"
                        :src="icons.SortingIcon"
                        :color="item.sortDirection === 'asc' ? 'green' : ''"
                        size="sm"
                      />
                      <OurSvgIcon
                        :src="icons.SortingIcon"
                        :color="item.sortDirection === 'desc' ? 'green' : ''"
                        size="sm"
                        class="-mr-2 -mt-3 -rotate-90"
                      />
                    </div>
                  </div>
                  <div v-else v-html="item.text"></div>
                </template>
              </th>
            </tr>

            <OurTopLoader
              v-if="loaderResourceName"
              class="table-loader"
              :resource-names="loaderResourceName"
              position="absolute"
            />
          </thead>
        </template>

        <template #row="{ row, rowIndex }">
          <tr
            v-if="rowIndex === firstRow && isShownSlot.beforeFirstRow"
            class="more-row"
            :class="beforeFirstRowClass()"
          >
            <td v-if="isShownSlot.beforeFirstRow" :colspan="colsCount" :class="firstRowClass">
              <!-- @slot Use it to add something before first row. -->
              <slot name="before-first-row" />
            </td>

            <td v-else class="first-row" />
          </tr>

          <DateSeparator
            v-if="isShownDateSeparator(rowIndex, row.date)"
            :class="dateSeparatorRowClass(rowIndex)"
            :date="row.date.primaryRow || row.date"
            :cols-count="colsCount"
            :i18n="i18n"
          />

          <DeliveryGroupSeparator
            v-if="isShownDeliveryGroupSeparator(rowIndex, row.deliveryGroup)"
            :class="deliveryGroupSeparatorRowClass(rowIndex)"
            :date="row.deliveryGroup.date"
            :warehouse="row.deliveryGroup.warehouse"
            :cols-count="colsCount"
          />

          <tr
            class="table-row"
            :class="[
              rowClass(row),
              {
                'table-row--static': !isRowInteractive,
                'table-row--navigable': isRowCellNavigable(row),
              },
            ]"
            :data-row-anchor-id="row.id"
            :data-cy="`${dataCy}-row`"
            @click="onTrClick(row, $event)"
          >
            <td v-if="selectable" class="col-checkbox">
              <TableCheckbox
                :values.sync="selectedRows"
                :value="rowIndex"
                :data-cy="`${dataCy}-table-body-checkbox`"
                :disabled="row.disabled"
                @click.native.stop="onClickSelectRow(rowIndex)"
              />
            </td>

            <td
              v-for="(value, key, index) in getFilteredRow(row)"
              :key="index"
              :data-cell-action="isNonNavigableColumn(key) ? 'true' : null"
              :class="[getTdClass(key), { 'table-data-cell': isCellNavigable(row, key) }]"
              @click.capture="onNavigableCellPointer(row, key, $event)"
            >
              <router-link
                v-if="isCellNavigable(row, key)"
                v-bind="getCellWrapperProps(row)"
                class="table-cell-navigation table-cell-navigation--overlay"
              />
              <div
                class="table-cell-content"
                :class="{ 'table-cell-content--navigable': isCellNavigable(row, key) }"
              >
                <template v-if="isExistSlot(`cell-${key}`)">
                  <!-- @slot Use it to customise table cell item (in whole column). -->
                  <slot :name="`cell-${key}`" :value="value" />
                </template>

                <template v-else-if="isExistSlot('cell-other')">
                  <slot name="cell-other" :value="value" />
                </template>

                <template v-else-if="key === 'date'">
                  <div v-if="value.format">
                    {{ dateConverter(value.timestamp, value.format) }}
                  </div>

                  <div v-else>
                    {{ dateConverter(value) }}
                  </div>
                </template>

                <template v-else-if="key === 'email'">
                  <div @click.stop>
                    <OurLink :text="value" :url="`mailto:${value}`" size="sm" />
                  </div>
                </template>

                <template v-else-if="key === 'link'">
                  <OurLink
                    v-if="value.route"
                    :data-cy="`${dataCy}-${key}-link`"
                    :text="value.label"
                    router-link
                    :route="value.route"
                    size="sm"
                  />

                  <span v-else>
                    {{ value }}
                  </span>
                </template>

                <template v-else-if="key === 'money'">
                  <OurMoney :sum="value.sum" :currency-symbol="value.currencySymbol" />
                </template>

                <template v-else-if="key === 'tags'">
                  <div class="tags-cell">
                    <OurTag
                      v-for="item in value.tags"
                      :key="item"
                      :text="item"
                      :color="value.variant"
                    />
                  </div>
                </template>

                <template
                  v-else-if="
                    value?.hasOwnProperty('secondaryRow') || value?.hasOwnProperty('thirdRow')
                  "
                >
                  <div class="primary-row" :data-cy="`${dataCy}-${key}-cell`">
                    {{ value.primaryRow || emptyItem }}
                  </div>

                  <div class="secondary-row">
                    <template v-if="Array.isArray(value.secondaryRow)">
                      <div v-for="(secondaryRow, idx) in value.secondaryRow" :key="idx">
                        <span class="children-empty">
                          {{ secondaryRow }}
                        </span>
                      </div>
                    </template>

                    <template v-else>
                      {{ value.secondaryRow }}
                    </template>
                  </div>

                  <div v-if="getThirdRowTags(value.thirdRow).length" class="mt-2 flex">
                    <OurTag
                      v-for="(tag, tagIndex) in getThirdRowTags(value.thirdRow)"
                      :key="tagIndex"
                      class="mr-1"
                      size="sm"
                      :text="tag.text"
                      :color="tag.color"
                    />
                  </div>
                </template>

                <template v-else>
                  <div :data-cy="`${dataCy}-${key}-cell`">{{ value || emptyItem }}</div>
                </template>
              </div>
            </td>
          </tr>

          <tr v-if="rowIndex === lastRow" class="more-row">
            <td v-if="isShownSlot.afterLastRow" :colspan="colsCount">
              <!-- @slot Use it to add something after last row. -->
              <slot name="after-last-row" />
            </td>
          </tr>
        </template>

        <template v-if="isExistSlot('tfoot')" #tfoot>
          <tfoot class="table-footer">
            <tr ref="footerRow" class="table-footer-row">
              <td v-if="selectable" />

              <!-- @slot Use it to add something in table footer. -->
              <slot name="tfoot" :cols-count="colsCount" />
            </tr>

            <tr ref="footerFixedRow" class="table-footer-fixed-row" :style="tableRowWidthStyle">
              <td v-if="selectable" />

              <!-- @slot Use it to add something in table footer. -->
              <slot name="tfoot" :cols-count="colsCount" />
            </tr>
          </tfoot>
        </template>

        <template v-if="!tableItems.length" #tbody>
          <tbody v-if="showSkeletonLoader">
            <tr v-for="tr in colsCount" :key="tr">
              <td v-if="selectable">
                <SkeletonLoader type="checkbox" />
              </td>
              <td v-for="td in headers" :key="td.value">
                <SkeletonLoader :type="td.value" />
              </td>
            </tr>
          </tbody>
          <EmptyTableMsg v-else :colspan="colsCount" :filters="filters" :i18n="i18n">
            <slot name="empty-table-msg" />
          </EmptyTableMsg>
        </template>
      </t-table>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import DateServiceDefault from "@/services/_bit/DateService";
import I18nServiceDefault from "@/services/_bit/I18nService";
import { getThemeClass } from "@/services/_bit/UiService";
import { isSameDay, isValid, secondsToMilliseconds } from "date-fns";
import SkeletonLoader from "@/ui/loader/SkeletonLoader";
import OurMoney from "@/components/_bit/TextMoney";
import OurTag from "@/components/_bit/TextTag";
import OurLink from "@/components/_bit/ButtonLink";
import OurCheckbox from "@/components/_bit/InputCheckbox";
import OurTopLoader from "@/layouts/_bit/_components/LoaderTop";
import TTable from "vue-tailwind/dist/t-table";
import TableCheckbox from "./components/TableCheckbox";
import EmptyTableMsg from "./components/EmptyTableMsg";
import DateSeparator from "./components/DateSeparator";
import DeliveryGroupSeparator from "./components/DeliveryGroupSeparator";

const ONE_REM = 16;

const DEFAULT_NON_NAVIGABLE_COLUMNS = new Set(["buttons", "quantity"]);

const TABLE_INTERACTIVE_TARGET_SELECTOR = [
  ".col-checkbox",
  'td[data-cell-action="true"]',
  "[data-table-action]",
  "button",
  "input",
  "textarea",
  "select",
  "label",
  "[role='button']",
  ".counter-wrapper",
  ".our-counter",
].join(", ");

export default {
  name: "OurTable",
  components: {
    OurTag,
    OurLink,
    OurMoney,
    OurCheckbox,
    OurTopLoader,
    TTable,
    TableCheckbox,
    EmptyTableMsg,
    DateSeparator,
    DeliveryGroupSeparator,
    SkeletonLoader,
  },

  props: {
    /**
     * Set items (rows) for table.
     */
    items: {
      type: Array,
      default: () => [],
      required: true,
    },

    canNavigate: {
      type: Boolean,
      default: true,
    },
    /**
     * Set headers for table.
     */
    headers: {
      type: Array,
      default: () => [],
      required: true,
    },

    /**
     * Set filters for table.
     */
    filters: {
      type: Object,
      default: () => ({}),
    },

    /**
     * It allows selecting table rows.
     */
    selectable: {
      type: Boolean,
      default: false,
    },

    /**
     * Shows selection counter header when rows are selected.
     */
    showSelectionHeader: {
      type: Boolean,
      default: false,
    },

    /**
     * Makes the table more narrow.
     */
    compact: {
      type: Boolean,
      default: false,
    },

    /**
     * Make footer fixed.
     */
    fixedFooter: {
      type: Boolean,
      default: false,
    },

    /**
     * Make header sticky.
     */
    stickyHeader: {
      type: Boolean,
      default: false,
    },

    hiddenHeader: {
      type: Boolean,
      default: false,
    },

    /**
     * Set loader resource name to activate table top loader exact for that request.
     */
    loaderResourceName: {
      type: String,
      default: "",
    },

    /**
     * Sets data-cy attribute for correct element sampling in tests.
     */
    dataCy: {
      type: String,
      default: "",
    },

    canSelectAllProps: {
      type: Boolean,
      default: true,
    },

    /**
     * When set, selection is tracked by item[key] values instead of row indices.
     * Enables cross-page selection when combined with selectedIds.
     */
    selectionKey: {
      type: String,
      default: "",
    },

    /**
     * Selected item values for selectionKey mode (controlled from parent).
     */
    selectedIds: {
      type: Array,
      default: () => [],
    },

    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    selectAll: false,
    canSelectAll: true,
    isApplyingSelection: false,
    selectedRows: [],
    tableItems: [],
    firstRow: 0,
    emptyItem: "–",
    tableWidth: 0,
    tableHeight: 0,
    pagePositionY: 0,
  }),

  computed: {
    ...mapState("layout", ["fixedAsideMenu"]),
    ...mapGetters("product", ["getTags", "getStatus"]),
    ...mapGetters("loaderTop", {
      globalLoading: "loading",
    }),

    showSkeletonLoader() {
      return this.isLoading || this.globalLoading;
    },

    icons: () => ({
      SortingIcon: require("@material-symbols/svg-400/outlined/chevron_left.svg"),
    }),

    theme() {
      return getThemeClass();
    },

    getTranslation() {
      return new I18nServiceDefault(this, { componentName: this.$options.name }).getTranslation;
    },

    i18n() {
      return {
        noItems: this.getTranslation("noItems"),
        noResultsForFilters: this.getTranslation("noResultsForFilters"),
        yesterday: this.getTranslation("yesterday"),
        today: this.getTranslation("today"),
        tomorrow: this.getTranslation("tomorrow"),
      };
    },

    isRowInteractive() {
      return this.canNavigate || !!this.$listeners.clickRow;
    },

    tableWrapClass() {
      const selected = this.hasSelectionActionsHeader ? "table-wrap-selected" : "";
      const compact = this.compact ? "table-wrap-compact" : "";

      return [selected, compact];
    },

    isSelectionByKey() {
      return !!this.selectionKey;
    },

    selectedRowsLength() {
      if (this.isSelectionByKey) {
        return this.selectedIds.length;
      }

      return this.selectedRows.length;
    },

    hasSelectionActionsHeader() {
      return (
        this.selectedRowsLength && (this.showSelectionHeader || this.isExistSlot("thead-actions"))
      );
    },

    isSelectedAllRows() {
      if (this.isSelectionByKey) {
        const selectableItems = this.tableItems.filter((item) => !item.disabled);

        if (!selectableItems.length) {
          return false;
        }

        const selectedSet = new Set(this.selectedIds);

        return selectableItems.every((item) => selectedSet.has(item[this.selectionKey]));
      }

      return this.selectedRowsLength === this.tableItems.length;
    },

    colsCount() {
      return this.headers.length + 1;
    },

    lastRow() {
      return this.items.length - 1;
    },

    isShownSlot() {
      return {
        beforeFirstRow: !!this.$slots["before-first-row"],
        afterLastRow: !!this.$slots["after-last-row"],
      };
    },

    tableClasses() {
      return {
        "fixed-footer": this.isFixedFooter,
      };
    },

    headerClasses() {
      return {
        compact: this.compact ? "compact-header" : "",
        actionsHeader: this.hasSelectionActionsHeader ? "actions-header" : "",
        fixedHeader: this.isFixedHeader && this.stickyHeader ? "table-fixed-header" : "",
        hideHeader: this.hasSelectionActionsHeader && !this.isFixedHeader ? "hide-header" : "",
      };
    },

    isShownFixedOrActionsHeader() {
      return this.isFixedHeader || this.hasSelectionActionsHeader;
    },

    isFixedFooter() {
      const windowHeight = window.innerHeight;

      return (
        windowHeight < this.tableHeight &&
        this.fixedFooter &&
        !this.isShownFooterPosition &&
        this.isCheckedMoreOneTableItems
      );
    },

    isFixedHeader() {
      return !this.isShownHeaderPosition;
    },

    isShownHeaderPosition() {
      const positionForFixHeader =
        this.$refs?.headerRow?.getBoundingClientRect().top + window.pageYOffset || 0;

      return positionForFixHeader >= this.pagePositionY;
    },

    isShownFooterPosition() {
      const windowHeight = window.innerHeight;
      const pageBottom = this.pagePositionY + windowHeight;
      const positionForFixFooter =
        this.$refs?.footerRow?.getBoundingClientRect().bottom + window.pageYOffset;

      return pageBottom >= positionForFixFooter;
    },

    isCheckedMoreOneTableItems() {
      const checkedTableItems = this.tableItems.filter((item) => item.isChecked);

      return checkedTableItems.length > 1;
    },

    tableRowWidthStyle() {
      return `width: ${this.tableWidth / ONE_REM}rem`;
    },

    firstRowClass() {
      let hasContentBeforeFirstRowSlot = false;

      this.$slots["before-first-row"]?.forEach((item) => {
        if (!hasContentBeforeFirstRowSlot) {
          hasContentBeforeFirstRowSlot = !!item.tag;
        }
      });

      return hasContentBeforeFirstRowSlot ? "" : "first-row";
    },
  },

  watch: {
    selectAll: {
      handler: "onChangeSelectAll",
      deep: true,
    },
    selectedRows: "onChangeSelectedRows",
    tableItems: {
      handler: "onChangeTableItems",
      deep: true,
    },
    items: {
      handler: "onChangeItems",
      deep: true,
    },
    selectedIds: "syncSelectionFromSelectedIds",
    fixedAsideMenu: "onChangeFixedAsideMenu",
    isFixedFooter: "onChangeFixedFooter",
    isFixedHeader: "onChangeFixedHeader",
  },

  created() {
    this.tableItems = this.items;

    document.addEventListener("keyup", this.onKeyupClearCheckbox);
    document.addEventListener("scroll", this.onScrollGetPosition);
  },

  updated() {
    this.tableHeight = this.$refs.tableWrapper.offsetHeight;
    this.tableWidth = this.$refs.tableWrapper.offsetWidth;
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.onKeyupClearCheckbox);
    document.removeEventListener("scroll", this.onScrollGetPosition);
  },

  methods: {
    normalizeThirdRow(thirdRow) {
      if (!thirdRow) return [];

      return Array.isArray(thirdRow) ? thirdRow.filter(Boolean) : [thirdRow];
    },

    getThirdRowTags(thirdRow) {
      return this.normalizeThirdRow(thirdRow)
        .map((row) => {
          if (row?.type === "preOrder") {
            const status = this.getStatus("preOrder");

            return status ? { text: status.label, color: status.color } : null;
          }

          const tag = this.getTags.find((item) => item.type === row?.type);

          if (!tag) return null;

          return {
            text: this.getTagText(tag, row),
            color: tag.color,
          };
        })
        .filter(Boolean);
    },

    getTagText(tag, thirdRow) {
      if (!tag) return "";

      if (tag.type === "bundleDiscount" || !thirdRow?.value) {
        return tag.value;
      }

      return `${tag.value} ${thirdRow.value} ${tag.symbol || ""}`;
    },

    isApplicationDetailsPage(page) {
      return page === "DealershipApplicationsDetails";
    },

    tagIsLink(row) {
      if (row.page && !this.isApplicationDetailsPage(row.page)) {
        return "a";
      } else {
        if (row.status?.value !== "rejected" && this.isApplicationDetailsPage(row.page)) {
          return "a";
        } else {
          return "div";
        }
      }
    },

    setRouteTo(row) {
      if (row.page && !this.isApplicationDetailsPage(row.page)) {
        return {
          name: row.page,
          params: { id: row.id, tab: row?.tab, options: row.options },
        };
      } else {
        if (row.status?.value !== "rejected" && this.isApplicationDetailsPage(row.page)) {
          return {
            name: row.page,
            params: { id: row.id, tab: row?.tab, options: row.options },
          };
        } else {
          return {
            name: this.$route.name,
            query: { ...this.$route.query },
          };
        }
      }
    },

    onChangeFixedFooter() {
      if (this.isFixedFooter) {
        this.$nextTick(() => {
          this.setFooterItemsWidth();
        });
      } else {
        this.setFooterItemsWidth(true);
      }
    },

    onChangeFixedHeader() {
      this.setHeaderItemsWidth();
    },

    setFooterItemsWidth(setNullWidth) {
      const ZERO_WIDTH = 0;

      if (!this.fixedFooter) return;

      const [...mainFooterItems] = this.$refs.footerRow.children;
      const [...fixedFooterItems] = this.$refs.footerFixedRow.children;

      fixedFooterItems.forEach((item, index) => {
        item.style.width = setNullWidth
          ? `${ZERO_WIDTH}rem`
          : `${mainFooterItems[index].offsetWidth / ONE_REM}rem`;
      });
    },

    setHeaderItemsWidth() {
      if (!this.hasSelectionActionsHeader && this.$refs.headerRow && this.$refs.headerFixedRow) {
        const [...mainHeaderItems] = this.$refs.headerRow.children;
        const [...fixedHeaderItems] = this.$refs.headerFixedRow.children;

        fixedHeaderItems.forEach((item, index) => {
          item.style.width = `${mainHeaderItems[index]?.offsetWidth / ONE_REM}rem`;
        });
      }
    },

    onChangeFixedAsideMenu() {
      this.tableHeight = this.$refs.tableWrapper.offsetHeight;
      this.tableWidth = this.$refs.tableWrapper.offsetWidth;

      this.setHeaderItemsWidth();
    },

    onScrollGetPosition() {
      this.pagePositionY = window.pageYOffset;
    },

    onChangeTableItems(tableItems) {
      this.$emit("update:items", tableItems);
    },

    onKeyupClearCheckbox(event) {
      const escKeyCode = 27;

      if (event.keyCode === escKeyCode && this.selectable) {
        if (this.isSelectionByKey) {
          this.updateSelectedIds([]);
        } else {
          this.selectedRows = [];
        }
      }
    },

    getCurrentPageSelectionValues() {
      return this.tableItems
        .filter((item) => !item.disabled)
        .map((item) => item[this.selectionKey]);
    },

    mergePageSelection(pageSelectedValues) {
      const currentPageIds = new Set(this.tableItems.map((item) => item[this.selectionKey]));

      const otherPagesIds = (this.selectedIds || []).filter((id) => !currentPageIds.has(id));

      return [...otherPagesIds, ...pageSelectedValues];
    },

    areArraysEqual(first, second) {
      if (first.length !== second.length) {
        return false;
      }

      return first.every((value, index) => value === second[index]);
    },

    updateSelectedRows(indices) {
      if (!this.areArraysEqual(indices, this.selectedRows)) {
        this.selectedRows = indices;
      }
    },

    updateSelectedIds(selectedIds) {
      const normalizedIds = selectedIds || [];

      if (this.areArraysEqual(normalizedIds, this.selectedIds)) {
        return;
      }

      this.$emit("update:selectedIds", normalizedIds);
      this.$emit("selectRows", normalizedIds);
      this.applySelection(normalizedIds);
    },

    syncSelectionFromSelectedIds() {
      this.applySelection(this.selectedIds);
    },

    applySelection(selectedIds) {
      if (!this.isSelectionByKey) {
        return;
      }

      const selectedIdSet = new Set(selectedIds || []);
      const key = this.selectionKey;
      const indices = [];

      this.tableItems.forEach((item, index) => {
        const isSelected = !item.disabled && selectedIdSet.has(item[key]);

        item.isChecked = isSelected;

        if (isSelected) {
          indices.push(index);
        }
      });

      this.updateSelectedRows(indices);

      const selectableItems = this.tableItems.filter((item) => !item.disabled);
      const nextSelectAll =
        selectableItems.length > 0 && selectableItems.every((item) => selectedIdSet.has(item[key]));

      if (this.selectAll !== nextSelectAll) {
        this.isApplyingSelection = true;
        this.selectAll = nextSelectAll;
        this.$nextTick(() => {
          this.isApplyingSelection = false;
        });
      }
    },

    onChangeItems() {
      if (this.isSelectionByKey) {
        this.tableItems = this.items;
        this.syncSelectionFromSelectedIds();

        return;
      }

      const checkedTableItems = this.tableItems.filter((item) => item.isChecked);

      this.tableItems = this.items;

      if (!this.items.length || this.tableItems.length !== this.items.length) {
        this.selectedRows = [];
      }

      this.tableItems.forEach((item) => {
        if (checkedTableItems.some((checkedItem) => item.id === checkedItem.id)) {
          item.isChecked = true;
        }
      });
    },

    isShownDateSeparator(rowIndex, date) {
      const index = !rowIndex ? rowIndex : rowIndex - 1;

      const prevItem = this.tableItems[index];
      const prevDateInMilliseconds = secondsToMilliseconds(prevItem?.date?.primaryRow);
      const dateInMilliseconds = secondsToMilliseconds(date?.primaryRow);
      const isSameDate = isSameDay(dateInMilliseconds, prevDateInMilliseconds);

      if (!rowIndex) {
        let hasContentBeforeFirstRowSlot = false;

        this.$slots["before-first-row"]?.forEach((item) => {
          if (!hasContentBeforeFirstRowSlot) {
            hasContentBeforeFirstRowSlot = !!item.tag;
          }
        });

        return isValid(prevDateInMilliseconds) && hasContentBeforeFirstRowSlot;
      }

      return isValid(prevDateInMilliseconds) && !isSameDate;
    },

    beforeFirstRowClass() {
      const isCheckedFirstRow = this.tableItems[0]?.isChecked;

      return isCheckedFirstRow ? "table-row-checked" : "";
    },

    rowClass(row) {
      return row.isChecked && !row.disabled ? "table-row-checked" : "";
    },

    dateSeparatorRowClass(rowIndex) {
      const isCheckedRowBefore = this.tableItems[rowIndex - 1]?.isChecked;
      const isCheckedRowAfter = this.tableItems[rowIndex]?.isChecked;

      return (isCheckedRowBefore && isCheckedRowAfter) || (rowIndex === 0 && isCheckedRowAfter)
        ? "table-row-checked"
        : "";
    },

    getDeliveryGroupKey(deliveryGroup) {
      if (!deliveryGroup?.date) {
        return "";
      }

      return `${deliveryGroup.date}-${deliveryGroup.warehouse || ""}`;
    },

    isShownDeliveryGroupSeparator(rowIndex, deliveryGroup) {
      if (!deliveryGroup?.date) {
        return false;
      }

      if (!rowIndex) {
        return true;
      }

      const prevGroup = this.tableItems[rowIndex - 1]?.deliveryGroup;

      return this.getDeliveryGroupKey(deliveryGroup) !== this.getDeliveryGroupKey(prevGroup);
    },

    deliveryGroupSeparatorRowClass(rowIndex) {
      const isCheckedRowBefore = this.tableItems[rowIndex - 1]?.isChecked;
      const isCheckedRowAfter = this.tableItems[rowIndex]?.isChecked;

      return (isCheckedRowBefore && isCheckedRowAfter) || (rowIndex === 0 && isCheckedRowAfter)
        ? "table-row-checked"
        : "";
    },

    onClickSelectRow(rowIndex) {
      const row = this.tableItems[rowIndex];

      if (row.disabled) {
        return;
      }

      const isRowChecked = row.isChecked;

      this.tableItems[rowIndex].isChecked = !isRowChecked;
    },

    getFilteredRow(row) {
      return Object.fromEntries(
        this.headers
          .filter((header) => Object.prototype.hasOwnProperty.call(row, header.value))
          .map((header) => [header.value, row[header.value]])
      );
    },

    onClickRow(row) {
      this.$emit("clickRow", row);
    },

    onTrClick(row, event) {
      if (this.canNavigate || this.isInteractiveTarget(event?.target)) {
        return;
      }

      this.onClickRow(row);
    },

    isRowCellNavigable(row) {
      return this.canNavigate && !!row.page;
    },

    isNonNavigableColumn(columnKey) {
      if (DEFAULT_NON_NAVIGABLE_COLUMNS.has(columnKey)) {
        return true;
      }

      const header = this.headers.find((item) => item.value === columnKey);

      return header?.navigable === false;
    },

    isCellNavigable(row, columnKey) {
      return this.isRowCellNavigable(row) && !this.isNonNavigableColumn(columnKey);
    },

    onNavigableCellPointer(row, columnKey, event) {
      if (!this.isCellNavigable(row, columnKey)) {
        return;
      }

      if (this.isInteractiveTarget(event.target)) {
        return;
      }

      if (this.$listeners.clickRow) {
        this.onClickRow(row);
      }
    },

    getCellWrapperProps(row) {
      return {
        to: this.setRouteTo(row),
        tag: this.tagIsLink(row),
      };
    },

    isInteractiveTarget(target) {
      if (!target?.closest) {
        return false;
      }

      if (target.closest(TABLE_INTERACTIVE_TARGET_SELECTOR)) {
        return true;
      }

      const link = target.closest("a");

      return Boolean(link && !link.classList.contains("table-cell-navigation"));
    },

    isExistSlot(slotName) {
      return !!this.$scopedSlots[slotName];
    },

    getTdClass(key) {
      return this.headers.find((item) => item.value === key)?.tdClass;
    },

    onChangeSelectAll(selectAll) {
      if (this.isApplyingSelection) {
        return;
      }

      if (this.isSelectionByKey) {
        const pageIds = this.getCurrentPageSelectionValues();
        const pageIdSet = new Set(pageIds);

        if (selectAll && this.canSelectAll) {
          this.updateSelectedIds(this.mergePageSelection(pageIds));
        } else if (!selectAll) {
          this.updateSelectedIds((this.selectedIds || []).filter((id) => !pageIdSet.has(id)));
        }

        this.canSelectAll = true;

        return;
      }

      if (selectAll && this.canSelectAll) {
        this.selectedRows = this.tableItems
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => !item.disabled)
          .map(({ index }) => index);

        this.tableItems.forEach((item) => {
          if (!item.disabled) {
            item.isChecked = true;
          }
        });
      } else if (!selectAll) {
        this.selectedRows = [];

        this.tableItems.forEach((item) => {
          item.isChecked = false;
        });
      }

      this.canSelectAll = true;
    },

    onChangeSelectedRows(selectedRows) {
      if (selectedRows.length) {
        this.canSelectAll = false;

        this.isCheckedMoreOneTableItems ? this.setFooterItemsWidth() : "";
      } else {
        this.$nextTick(() => {
          this.setHeaderItemsWidth();
        });
      }

      if (this.isSelectionByKey) {
        const key = this.selectionKey;
        const selectedOnPageIds = selectedRows
          .map((index) => this.tableItems[index]?.[key])
          .filter((id) => id !== undefined && id !== null);

        this.updateSelectedIds(this.mergePageSelection(selectedOnPageIds));
      } else {
        this.$emit("selectRows", selectedRows);
        this.selectAll = !!selectedRows.length;
      }
    },

    dateConverter(value, format) {
      return new DateServiceDefault().dateConverter(value, format);
    },

    clearSelectedItems() {
      if (this.isSelectionByKey) {
        this.updateSelectedIds([]);
      } else {
        this.selectedRows = [];
      }
    },
  },
};
</script>

<i18n>
en:
  noItems: There is no data in the table yet.
  noResultsForFilters: No results were found for the specified filters.
  yesterday: Yesterday
  today: Today
  tomorrow: Tomorrow
ru:
  noItems: Пока что в таблице нет данных.
  noResultsForFilters: По заданным фильтрам ничего не найдено.
  yesterday: Вчера
  today: Сегодня
  tomorrow: Завтра
ua:
  noItems: Поки що в таблиці немає даних.
  noResultsForFilters: За даними фільтрами нічого не знайдено.
  yesterday: Вчора
  today: Сьогодні
  tomorrow: Завтра
</i18n>

<style lang="postcss" scoped>
.table-wrapper {
  @apply relative;
}

.more-row {
  .first-row {
    @apply py-1 !important;
  }

  .last-row {
    @apply py-1 !important;
  }

  td {
    @apply px-0 py-4 !important;

    & :deep:first-child {
      @apply mx-auto;
    }
  }
}

.table-fixed-header {
  @apply fixed top-0 border border-t-0 border-gray-200;
  @apply bg-white;

  &.actions-header {
    @apply rounded-t-none;
  }
}

.actions-header {
  @apply rounded-t-lg border border-blue-200 bg-blue-50;

  .selected-rows-count {
    @apply flex items-center;
    @apply font-medium text-gray-900;
  }

  .actions-slot-wrap {
    @apply flex items-center space-x-1 pl-4;
  }
}

.table-custom-header {
  @apply flex items-center;
  @apply z-10;

  &-item {
    @apply flex-none;
    @apply text-sm font-normal text-gray-500;
    @apply px-[1.125rem] py-5;

    &:first-child {
      @apply p-5;
    }

    &:nth-child(2) {
      @apply pl-0;
    }

    &:last-child {
      @apply p-5;
    }
  }

  &.compact-header {
    .table-custom-header-item {
      @apply px-3 py-4;

      &:last-child,
      &:first-child {
        @apply p-4;
      }
    }
  }
}

.table-wrap {
  @apply overflow-x-auto overflow-y-hidden rounded-lg;
  @apply border border-gray-200;

  &-selected {
    @apply !rounded-t-none border-t-0;
  }

  .our-table:deep() {
    @apply min-w-full border-none bg-white text-sm;
    @apply w-full;

    thead {
      @apply border-b border-solid border-gray-200;

      tr {
        th {
          @apply bg-white px-[1.125rem] py-5;
          @apply text-sm font-normal text-gray-500;

          &:first-child,
          &:last-child {
            @apply p-5;
          }

          &:nth-child(2) {
            @apply pl-0;
          }
        }
      }
    }

    tbody {
      @apply divide-none;

      tr {
        td {
          @apply text-ellipsis p-[1.125rem] py-5;
          @apply align-top;
          overflow-wrap: break-word;

          &:first-child,
          &:last-child {
            @apply p-5;
          }

          &:nth-child(2) {
            @apply pl-0;
          }

          .tags-cell {
            @apply flex flex-wrap items-start gap-x-2 gap-y-2;
          }

          :deep(.tag) {
            @apply inline-flex w-fit max-w-full;
          }
        }
      }
    }

    tfoot {
      @apply border-t border-solid border-gray-200;

      tr {
        td {
          @apply p-[1.125rem] py-5;

          &:first-child {
            @apply p-5;
          }

          &:nth-child(2) {
            @apply pl-0;
          }
        }
      }
    }

    .secondary-row {
      @apply mt-1 text-xs text-gray-500;
    }

    tr.table-row--navigable {
      td[data-cell-action="true"] {
        @apply relative z-[2];
      }

      td.table-data-cell {
        @apply relative p-0 align-top;

        .table-cell-navigation--overlay {
          @apply absolute inset-0 z-0 block no-underline;
          color: inherit;
        }

        .table-cell-content--navigable {
          @apply relative z-[1] box-border block w-full;
          @apply p-[1.125rem] py-5;
          pointer-events: none;

          :deep(*) {
            pointer-events: none;
          }

          :deep(a),
          :deep(button),
          :deep(input),
          :deep(textarea),
          :deep(select),
          :deep(label),
          :deep([role="button"]),
          :deep([data-table-action]) {
            pointer-events: auto;
          }
        }

        &:last-child .table-cell-content--navigable {
          @apply p-5;
        }

        &:nth-child(2) .table-cell-content--navigable {
          @apply pl-0;
        }
      }
    }
  }

  &-compact {
    .our-table:deep() {
      thead {
        tr {
          th {
            @apply bg-white p-4;

            &:first-child,
            &:last-child {
              @apply p-4;
            }
          }
        }
      }

      tbody {
        tr {
          td {
            @apply px-4 py-3;

            &:first-child,
            &:last-child {
              @apply px-4 py-3;
            }
          }

          &.table-row--navigable td.table-data-cell .table-cell-content--navigable {
            @apply px-4 py-3;
          }
        }
      }

      tfoot {
        tr {
          td {
            @apply p-4;

            &:first-child {
              @apply p-4;
            }
          }
        }
      }
    }
  }
}

.col-checkbox {
  @apply w-[3.75rem];
}

.table-row {
  @apply cursor-pointer transition duration-100;
  @apply hover:bg-gray-50;

  &--static {
    @apply cursor-default hover:bg-white;
  }

  &-checked {
    @apply transition duration-100;
    @apply bg-gray-100;
  }
}

.children-empty {
  @apply inline-block;
}

.fixed-footer {
  .table {
    &-footer {
      @apply relative;

      &-fixed-row {
        visibility: inherit;
      }
    }
  }
}

.hide-header {
  @apply relative -top-[3.75rem];
  visibility: collapse;
}

.table-footer-fixed-row {
  @apply fixed bottom-0 -ml-px;
  @apply border border-b-0 border-gray-200 bg-white;
  visibility: collapse;
}

.table-loader {
  @apply !top-auto;
}

.fixed-header-table-loader {
  @apply !top-[3.75rem];
}

.table-checkbox {
  @apply w-[3.75rem];
}
</style>
